import { Camera, ImagePlus, Upload, X } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button } from 'v5/design-system/Button';
import { ImagePreview } from 'v5/design-system/ImagePreview';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { cn } from 'v5/platform/dom/cn';
import { useFileInput } from 'v5/platform/react/useFileInput';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { PasteButton } from './PasteButton';
import type { UploadedMedia } from './uploadedMedia';
import { UploadProgressBar } from './UploadProgress';
import { useClipboardFiles } from './useClipboardFiles';
import { useMediaUploads } from './useMediaUploads';

const MAX_SIZE = 10 * 1024 * 1024;

export type SingleImageUploaderProps = WithServices<
  [WithUploadService, WithToastService]
> & {
  /** The image shown on the tile; `null` = none. */
  imageUrl: string | null;
  /** The upload finished; its local preview is the receiver's to keep or revoke. */
  onUploaded(media: UploadedMedia): void;
  onRemove(): void;
  /** Lets the host hold its steps and its save while an upload runs. */
  onUploadingChange?(uploading: boolean): void;
  /** Content of the empty tile (e.g. initials); an image icon by default. */
  placeholder?: ReactNode;
  /** `contain` keeps a logo whole instead of cropping it to the square tile. */
  imageFit?: 'cover' | 'contain';
  disabled?: boolean;
  className?: string;
};

/**
 * One image as an avatar uploader: the whole field takes a dropped image, the
 * tile opens the picker, Paste reads the clipboard, and ⌘/Ctrl+V pastes while
 * focus is in the field (so several uploaders on one screen never compete for
 * one paste).
 */
export function SingleImageUploader({
  services,
  imageUrl,
  onUploaded,
  onRemove,
  onUploadingChange,
  placeholder,
  imageFit = 'cover',
  disabled = false,
  className
}: SingleImageUploaderProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ImageUploadField' });
  const { uploadService, toastService } = services;
  const uploads = useMediaUploads(uploadService);
  const pending = uploads.uploads[0];
  const isUploading = uploads.isUploading;
  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);
  const busy = disabled || isUploading;
  const shownImage = pending?.previewUrl ?? imageUrl;

  const handleFile = async (file: File | undefined) => {
    if (!file || busy) return;
    if (!file.type.startsWith('image/') || file.size > MAX_SIZE) {
      toastService.open({ type: 'error', message: t('uploadError') });
      return;
    }
    const outcome = await uploads.upload(file);
    switch (outcome.type) {
      case 'uploaded':
        onUploaded(outcome.media);
        return;
      case 'failed': {
        toastService.open({ type: 'error', message: t('uploadError') });
        uploads.dismiss(outcome.id);
        return;
      }
    }
  };

  const openPicker = useFileInput({
    accept: 'image/*',
    onFileSelected: file => handleFile(file)
  });

  const clipboard = useClipboardFiles({
    accepts: file => file.type.startsWith('image/'),
    onFiles: files => void handleFile(files[0])
  });

  const pasteFromClipboard = async () => {
    switch (await clipboard.pasteFromClipboard()) {
      case 'failed':
        toastService.open({ type: 'error', message: t('pasteFailed') });
        return;
      case 'empty':
        toastService.open({ type: 'warning', message: t('pasteEmpty') });
        return;
      case 'pasted':
        return;
    }
  };

  return (
    <Dropzone
      onDrop={(accepted: File[], rejected: File[]) =>
        void handleFile(accepted[0] ?? rejected[0])
      }
      multiple={false}
      disableClick
      disabled={busy}
      className={cn('w-full', className)}
    >
      {({ isDragActive }) => (
        <div
          className="flex items-center gap-5"
          onPaste={busy ? undefined : clipboard.onPaste}
          data-testid="image-upload-field"
        >
          <button
            type="button"
            disabled={busy}
            onClick={openPicker}
            aria-label={imageUrl ? t('change') : t('upload')}
            aria-busy={isUploading}
            className={cn(
              'group relative size-24 shrink-0 cursor-pointer overflow-hidden rounded-xl outline-none transition-all',
              'ring-offset-bg-default focus-visible:ring-2 focus-visible:ring-bg-emphasis/70 focus-visible:ring-offset-2',
              shownImage !== null
                ? 'bg-bg-subtle'
                : 'border border-dashed border-border-default bg-bg-subtle hover:border-bg-inset',
              isDragActive && 'border-bg-inset ring-2 ring-bg-emphasis/40',
              busy && 'cursor-not-allowed'
            )}
            data-testid="image-upload-tile"
          >
            {shownImage !== null ? (
              <ImagePreview
                src={shownImage}
                objectFit={imageFit}
                className={cn(
                  'size-full transition-opacity',
                  isUploading && 'opacity-60'
                )}
              />
            ) : (
              <div className="flex size-full items-center justify-center text-xl font-medium text-fg-muted">
                {placeholder ?? <ImagePlus className="size-7" />}
              </div>
            )}
            {pending?.status === 'uploading' ? (
              <UploadProgressBar
                progress={pending.progress}
                className="absolute inset-x-2.5 bottom-2.5"
              />
            ) : (
              <div
                className={cn(
                  'absolute inset-0 flex items-center justify-center bg-bg-emphasis/50 text-white transition-opacity',
                  isDragActive
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100'
                )}
              >
                <Camera className="size-6" />
              </div>
            )}
          </button>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="text-sm text-fg-muted">
              <p>{t('hint')}</p>
              <p>{t('formats')}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={openPicker}
              >
                {isUploading ? (
                  <>
                    <LoadingSpinner className="size-4" />
                    {t('uploading')}
                  </>
                ) : (
                  <>
                    <Upload className="size-4" />
                    {imageUrl ? t('change') : t('upload')}
                  </>
                )}
              </Button>
              {clipboard.canPaste && (
                <PasteButton
                  label={t('paste')}
                  disabled={busy}
                  onClick={() => void pasteFromClipboard()}
                />
              )}
              {imageUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={onRemove}
                >
                  <X className="size-4" />
                  {t('remove')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Dropzone>
  );
}
